import {
  ExpandedQuote,
  ICallOptimal,
  IExpandedQuote,
  IOptionChain,
  IPutOptimal,
  OptionChain,
  Predictions,
  StandardDeviation,
} from '@op/shared/src/models/how';
import { IHow, IHowCategory, IHowStrategy, IHowStrategyLeg } from '@op/shared/src/models/how/how-entity';
import { ITradingStrategy } from '@op/shared/src/models/how/trading-strategy';
import { SentimentType } from '../enums/enums';
import ApplicationContext from './application-context';
import { IStrategyStrategy } from '../configuration/strategy';

import DateTimeHelper from './date-time-helper';

// ---------------------------------------------------------------------------
// F1 — HowCategory model class (factory pattern).
// ---------------------------------------------------------------------------

/**
 * F1 — Model class for a configurable HOW category.
 * Use `HowCategory.fromData(raw)` to construct from API / hardcoded data.
 */
export class HowCategory implements IHowCategory {
  readonly name: string;
  readonly strategies: IHowStrategy[];
  /** Explicit market outlook (WP3). Omitted when not set. */
  readonly sentiment?: string;
  /** Explicit what-if defaults (WP3). Omitted when not set. */
  readonly whatIf?: { priceType?: string; priceSd?: number };

  private constructor(
    name: string,
    strategies: IHowStrategy[],
    sentiment?: string,
    whatIf?: { priceType?: string; priceSd?: number },
  ) {
    this.name = name;
    this.strategies = strategies;
    this.sentiment = sentiment;
    this.whatIf = whatIf;
  }

  /** Creates a HowCategory instance from a raw IHowCategory data object. */
  static fromData(data: IHowCategory): HowCategory {
    return new HowCategory(data.name, [...data.strategies], data.sentiment, data.whatIf);
  }

  /** Convenience: maps an array of raw category data into HowCategory instances. */
  static fromDataArray(data: IHowCategory[]): HowCategory[] {
    return data.map(HowCategory.fromData);
  }
}

/**
 * WP3 — resolve a category's effective market outlook: use the explicitly configured
 * sentiment when present, else fall back to today's name-based guess (unknown names → BULLISH).
 * Implemented as a free function because `howData.categories` holds plain IHowCategory objects
 * (via processCategories spread), not HowCategory class instances.
 */
export const effectiveSentiment = (category: IHowCategory, howData: HowDataModel): SentimentType =>
  howData.toSentimentType(category.sentiment || category.name);

const getNonStandardPremiumRows = (optionChains: IOptionChain[]): IOptionChain[] => {
  const result: IOptionChain[] = [];

  for (const chain of optionChains) {
    const callPM = chain.callOption?.premiumMultiplier;
    const putPM = chain.putOption?.premiumMultiplier;

    if (callPM !== 100 || putPM !== 100) {
      result.push(chain);
    }
  }

  return result;
};

class HowDataModel {
  readonly symbol;
  private readonly _stdDev: StandardDeviation;
  private readonly _predictions: Predictions;
  quote: IExpandedQuote;
  tradingStrategiesRaw: Map<SentimentType, ITradingStrategy[]> = new Map();
  originalSentiment: SentimentType;
  optionType = 'S';
  chain: OptionChain | undefined;
  callOptimals: ICallOptimal[] = [];
  putOptimals: IPutOptimal[] = [];
  alternativeCall: ICallOptimal | undefined;
  alternativePut: IPutOptimal | undefined;
  hasOption: boolean = false;
  isTradeable: boolean = false;
  /** F1 — dynamic categories. Populated from live API. */
  categories: IHowCategory[] = [];

  constructor(how: IHow, symbol: string) {
    this.symbol = symbol;
    this.quote = ExpandedQuote.fromSelf(how.expandedQuote);
    this.originalSentiment = this.toSentimentType(how.sentiment);
    this.callOptimals = how.callOptimals.sort((first, second) => first.priority - second.priority);
    this.putOptimals = how.putOptimals.sort((first, second) => first.priority - second.priority);
    this.alternativeCall = how.alternativeCall;
    this.alternativePut = how.alternativePut;
    this.hasOption = how.expandedQuote.hasOption;
    this.isTradeable = how.expandedQuote.isTradeable;
    this._stdDev = new StandardDeviation(
      how.expandedQuote.symbol || '',
      how.expandedQuote.last || 0,
      how.standardDeviations,
    );
    this._predictions = new Predictions(how.expandedQuote.symbol || '', how.predictions);
    this.tradingStrategiesRaw.set(SentimentType.BULLISH, how.tradingStrategies);
    this.tradingStrategiesRaw.set(SentimentType.BEARISH, how.oppositeStrategies);
    this.tradingStrategiesRaw.set(SentimentType.NEUTRAL, how.neutralStrategies);
    const chains = this.getOptionChainData(how, 'both');
    if (how.optionChains && how.optionChains.length > 0 && chains.rows.length > 0) {
      // takes the chains from the how API and loads them into an array of objects represented as rows
      this.chain = chains;
      this.quote.hasOption = true;
    }
    const rawCategories = how.categories && how.categories.length > 0 ? how.categories : [];
    this.categories = this.processCategories(rawCategories);
  }

  private processCategories(categories: IHowCategory[]): IHowCategory[] {
    const allowedStrategies = ApplicationContext.configuration?.strategiesConfiguration?.allowedStrategies;
    if (!allowedStrategies) {
      return categories;
    }

    return categories.map((category) => {
      const updatedStrategies = category.strategies.map((strategy) => {
        if (!strategy.isIncome) {
          return strategy;
        }

        if (!strategy.legs || strategy.legs.length === 0) {
          return strategy;
        }

        const hasSecurityLeg = strategy.legs?.some((leg) => leg.legType === 'Security' || leg.legType === 'Stock');
        if (hasSecurityLeg) {
          return strategy;
        }

        let templateStrategy: IStrategyStrategy | undefined;
        for (const allowed of allowedStrategies) {
          const found = allowed.strategies.find(
            (s) => s.name === strategy.strategyName || s.strategy.name === strategy.strategyName,
          );
          if (found) {
            templateStrategy = found.strategy;
            break;
          }
        }

        if (templateStrategy) {
          const securityTemplateLeg = templateStrategy.legs.find(
            (l) => l.legType === 'Security' || l.legType === 'Stock',
          );
          if (securityTemplateLeg) {
            const newLeg: IHowStrategyLeg = {
              legType: securityTemplateLeg.legType,
              quantity:
                securityTemplateLeg.buyOrSell === 'Sell' ? -securityTemplateLeg.quantity : securityTemplateLeg.quantity,
              expiry: null,
              strikePrice: null,
            };
            const legs = strategy.legs ? [...strategy.legs] : [];
            legs.push(newLeg);
            return {
              ...strategy,
              legs,
            };
          }
        }

        return strategy;
      });

      return {
        ...category,
        strategies: updatedStrategies,
      };
    });
  }

  getOptionChainData = (how: IHow, option: 'onlyNonSTD' | 'both') => {
    const nonStdOptions = getNonStandardPremiumRows(how.optionChains);

    const result: Record<string, Set<string>> = {}; // Changed to store strings like "CALL:120"

    nonStdOptions.forEach((item) => {
      const formattedExpiry = DateTimeHelper.format(item.expiry);

      if (!result[formattedExpiry]) {
        result[formattedExpiry] = new Set();
      }

      // Check call option
      if (item.callOption && item.callOption.premiumMultiplier !== 100) {
        const multiplier = item.callOption.premiumMultiplier;
        result[formattedExpiry].add(`CALL - ${item.strikePrice} - ${multiplier}`);
      }

      // Check put option
      if (item.putOption && item.putOption.premiumMultiplier !== 100) {
        const multiplier = item.putOption.premiumMultiplier;
        result[formattedExpiry].add(`PUT - ${item.strikePrice} - ${multiplier}`);
      }
    });

    const chainData = OptionChain.fromData(
      how.expandedQuote.symbol,
      option === 'both' ? how.optionChains : nonStdOptions,
      how.expandedQuote.last,
    );

    return chainData;
  };

  get stdDev() {
    return this._stdDev;
  }

  get predictions() {
    return this._predictions;
  }

  toSentimentType = (sentiment: string) => {
    switch (sentiment.trim().toUpperCase()) {
      case SentimentType.ALL:
        return SentimentType.ALL;
      case SentimentType.BEARISH:
        return SentimentType.BEARISH;
      case SentimentType.BULLISH:
        return SentimentType.BULLISH;
      case SentimentType.NEUTRAL:
        return SentimentType.NEUTRAL;
      default:
        return SentimentType.BULLISH;
    }
  };
}

export default HowDataModel;
